import { getFromServerTopic } from "@utility/slice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IDLE, LOADING } from "@constants";

const SLICE = "portfolioReporting";

const action = (name) => `${SLICE}/${name}`;

export const getFromServer = (action, contextOptionsFn, payloadFn) =>
  getFromServerTopic({ topic: SLICE, action, contextOptionsFn, payloadFn });

const getReportingYears = getFromServer(
  "getReportingYears",
  (payload) => payload,
  (payload) => payload,
);

const getPortfolioReportingDataForYear = getFromServer(
  "getPortfolioReportingDataForYear",
  (payload) => payload,
  (payload) => payload,
);

export const getPortfolioReportingData = createAsyncThunk(
  action("getPortfolioReportingData"),
  async ({ customerId, year }, { dispatch }) => {
    const result = await dispatch(
      getPortfolioReportingDataForYear({
        customerId,
        year,
      }),
    );
    return result.payload;
  },
);

export const getPriorYearPortfolioReportingData = createAsyncThunk(
  action("getPriorYearPortfolioReportingData"),
  async (
    { comparisonYear, customerId, adjustForWeatherYear },
    { dispatch },
  ) => {
    const result = await dispatch(
      new getPortfolioReportingDataForYear({
        customerId,
        year: comparisonYear,
        adjustForWeatherYear,
      }),
    );
    return result.payload;
  },
);

export const getPortfolioReportingDataForCustomerId = createAsyncThunk(
  action("getPortfolioReportingDataForCustomerId"),
  async (
    { adjustForWeatherYear, customerId, comparisonYear, year },
    { dispatch },
  ) => {
    const result = await dispatch(
      getPortfolioReportingDataForYear({
        customerId,
        year,
      }),
    );

    const comparisonResult = (
      comparisonYear
        ? await dispatch(
            new getPortfolioReportingDataForYear({
              customerId,
              year: comparisonYear,
              adjustForWeatherYear,
            }),
          )
        : null
    )?.payload;

    return { data: result.payload, comparison: comparisonResult };
  },
);

export const initializePage = createAsyncThunk(
  action("initializePage"),
  async (_payload, { dispatch, getState }) => {
    const persistedAppData = getState().persistedAppData;
    const userInfo = persistedAppData.userinfo;
    const customers = persistedAppData.lookupdata.organizations;

    const customerId = userInfo.acl.roles.isEmployee
      ? userInfo.customerId
      : null;

    const reportingYears = (await dispatch(getReportingYears())).payload
      .response;

    const now = new Date();
    const year = now.getFullYear() - (now.getMonth() === 0 ? 1 : 0);
    const comparisonYear = year - 1;

    const getData = dispatch(
      getPortfolioReportingDataForYear({
        customerId,
        year,
      }),
    );

    const getComparison = reportingYears.find((it) => it === comparisonYear)
      ? dispatch(
          getPortfolioReportingDataForYear({
            customerId,
            year: comparisonYear,
          }),
        )
      : Promise.resolve();

    const [data, comparisonData] = await Promise.all([getData, getComparison]);

    return {
      response: {
        customerId,
        customers,
        year,
        comparisonYear,
        comparisonData,
        data,
      },
    };
  },
);

export const slice = createSlice({
  name: SLICE,
  initialState: {},
  reducers: {
    removeComparison: (state) => {
      state.comparisonData = undefined;
      state.comparisonYear = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReportingYears.fulfilled, (state, action) => {
        const years = action.payload.response;
        const sortedYears = [...years];
        sortedYears.sort().reverse();

        state.years = sortedYears;
        state.year = sortedYears[0];
      })
      .addCase(getPortfolioReportingData.pending, (state, action) => {
        state.status = LOADING;
      })
      .addCase(getPortfolioReportingData.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { customerId, year } = meta.arg;

        state.customerId = customerId;
        state.data = payload.response;
        state.status = IDLE;
        state.year = year;
      })
      .addCase(getPortfolioReportingDataForCustomerId.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(
        getPortfolioReportingDataForCustomerId.fulfilled,
        (state, action) => {
          const { meta, payload } = action;
          const { customerId } = meta.arg;
          const { data, comparison } = payload;

          state.customerId = customerId;
          state.data = data.response;
          state.comparisonData = comparison.response;
          state.status = IDLE;
        },
      )
      .addCase(getPriorYearPortfolioReportingData.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(
        getPriorYearPortfolioReportingData.fulfilled,
        (state, action) => {
          const { meta, payload } = action;
          const { comparisonYear } = meta.arg;

          state.comparisonData = payload.response;
          state.comparisonYear = comparisonYear;
          state.status = IDLE;
        },
      )
      .addCase(initializePage.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(initializePage.fulfilled, (state, action) => {
        const {
          comparisonData,
          comparisonYear,
          customerId,
          customers,
          data,
          year,
        } = action.payload.response;

        state.comparisonData = comparisonData?.payload?.response;
        state.comparisonYear = comparisonData ? comparisonYear : undefined;
        state.customerId = customerId;
        state.customers = customers;
        state.data = data.payload.response;
        state.status = IDLE;
        state.year = year;
      });
  },
});

export const { removeComparison } = slice.actions;

export default slice.reducer;
